import { useEffect, useRef, useState } from "react";
import { packVideo } from "@/lib/packVideo";
import { Button } from "@/components/ui/button";
import { BookOpen, Download, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

function ShareBar({ sectorId, mp4 }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}${mp4.split("?")[0]}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Video link copied — paste it into LinkedIn or an email"); trackEvent("pack_video_copy", { sector: sectorId }); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Couldn't copy — long-press the video to save it instead"); }
  };
  return (
    <div className="mt-3 flex flex-wrap gap-2" data-testid={`pack-video-share-${sectorId}`}>
      <Button asChild size="sm" variant="outline" data-testid={`pack-video-download-${sectorId}`}>
        <a href={mp4} download={`futuregen-${sectorId}-policy-pack.mp4`} onClick={() => trackEvent("pack_video_download", { sector: sectorId })}><Download className="w-3.5 h-3.5 mr-1.5" /> Download video</a>
      </Button>
      <Button size="sm" variant="outline" onClick={copy} data-testid={`pack-video-copy-${sectorId}`}>
        {copied ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <Link2 className="w-3.5 h-3.5 mr-1.5" />} {copied ? "Copied" : "Copy link"}
      </Button>
    </div>
  );
}

export default function PackFlickthrough({ sectorId, className = "", caption = "Flick through the real pack", share = false }) {
  const ref = useRef(null);
  const v = packVideo(sectorId);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => { e.isIntersecting ? el.play().catch(() => {}) : el.pause(); }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, [sectorId]);

  return (
    <div className={className}>
      <div className="relative rounded-2xl overflow-hidden border border-border shadow-lg bg-[#0f172a] aspect-video" data-testid={`pack-video-${sectorId}`}>
        <video key={sectorId} ref={ref} muted loop playsInline autoPlay preload="metadata" poster={v.poster} className="w-full h-full object-cover" data-testid={`pack-video-player-${sectorId}`}>
          <source src={v.mp4} type="video/mp4" />
        </video>
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur px-2.5 py-1 text-[11px] font-semibold text-white" data-testid={`pack-video-caption-${sectorId}`}>
          <BookOpen className="w-3 h-3" /> {caption}
        </span>
      </div>
      {share && <ShareBar sectorId={sectorId} mp4={v.mp4} />}
    </div>
  );
}
